// src/components/CrashCourse/FinalExam/FinalExamGateway.jsx
//
// Shown once a student has completed every module in the course. Confirms
// they're qualified to sit the Final Certification Exam and states the
// rules (question count, passing score, retake policy) before they start.

import React from "react";
import { Shield, CheckCircle2, ArrowLeft, ArrowRight, AlertTriangle } from "lucide-react";

export default function FinalExamGateway({
  courseTitle,
  moduleTitles = [],
  questionCount = 50,
  passThreshold = 80,
  studentEmail,
  onStart,
  onReview,
  isDark,
}) {
  const D       = isDark;
  const panel   = D ? "#142a4d" : "#ffffff";
  const border  = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text    = D ? "#f0f4f8" : "#0d1f3c";
  const textMid = D ? "rgba(240,244,248,0.80)" : "#334155";
  const textSub = D ? "rgba(240,244,248,0.55)" : "#64748b";
  const gold    = "#c9971c";
  const goldL   = "#fbbf24";

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "clamp(24px,5vw,60px) clamp(14px,4vw,28px)" }}>
      <div style={{ textAlign: "center", marginBottom: 26 }}>
        <div style={{
          width: 64, height: 64, borderRadius: 18, margin: "0 auto 18px",
          background: `linear-gradient(135deg,${gold},${goldL})`, display: "flex",
          alignItems: "center", justifyContent: "center", color: "#0d1f3c",
          boxShadow: `0 8px 24px rgba(201,151,28,0.35)`,
        }}>
          <Shield size={28} />
        </div>
        <h1 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px,3.4vw,30px)", fontWeight: 900, color: text, margin: "0 0 8px" }}>
          You're Qualified for the Final Exam
        </h1>
        <p style={{ color: textSub, fontSize: 14.5, maxWidth: 480, margin: "0 auto", lineHeight: 1.6 }}>
          You've completed every module of the {courseTitle}. You are now qualified to take the official
          PPCA Final Certification Exam.
        </p>
      </div>

      <div style={{ background: panel, borderRadius: 16, border: `1px solid ${border}`, padding: "22px 24px", marginBottom: 18 }}>
        <div style={{ fontWeight: 800, fontSize: 14, color: text, marginBottom: 14 }}>Course completion checklist</div>
        {moduleTitles.map((title, i) => (
          <div key={title} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "9px 0",
            borderBottom: i < moduleTitles.length - 1 ? `1px solid ${border}` : "none",
          }}>
            <CheckCircle2 size={16} style={{ color: "#34d399", flexShrink: 0 }} />
            <span style={{ fontSize: 13.5, color: textMid }}>{title}</span>
          </div>
        ))}
      </div>

      <div style={{ background: panel, borderRadius: 16, border: `1px solid ${border}`, padding: "22px 24px", marginBottom: 24 }}>
        <div style={{ fontWeight: 800, fontSize: 14, color: text, marginBottom: 14 }}>Final exam rules</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 14 }}>
          {[
            [String(questionCount), "Total questions"],
            [`${passThreshold}%`, `Passing score (${Math.round(questionCount * passThreshold / 100)}/${questionCount})`],
            ["Unlimited", "Retakes if needed"],
            ["~90 min", "Suggested time"],
          ].map(([big, small]) => (
            <div key={small}>
              <div style={{ fontSize: 20, fontWeight: 900, color: goldL }}>{big}</div>
              <div style={{ fontSize: 12, color: textSub }}>{small}</div>
            </div>
          ))}
        </div>

        <div style={{
          display: "flex", gap: 10, alignItems: "flex-start", background: "rgba(201,151,28,0.10)",
          border: "1px solid rgba(201,151,28,0.25)", borderRadius: 12, padding: "13px 16px", marginTop: 16,
        }}>
          <AlertTriangle size={16} style={{ color: goldL, flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: goldL, lineHeight: 1.6 }}>
            Treat this as the real thing. On passing, your <strong>PPCA Certified</strong> badge is added to your
            profile, and a downloadable, shareable Certificate of Completion with a verification QR code is emailed
            to <strong>{studentEmail}</strong>.
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center" }}>
        {onReview && (
          <button onClick={onReview} style={btnGhost(D)}>
            <ArrowLeft size={15} /> Review Course Materials
          </button>
        )}
        <button onClick={onStart} style={btnGold(gold, goldL)}>
          I'm Ready — Start Final Exam <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

const btnBase = {
  cursor: "pointer", border: "none", borderRadius: 12, fontWeight: 800, fontSize: 14,
  padding: "13px 24px", display: "inline-flex", alignItems: "center", justifyContent: "center",
  gap: 8, fontFamily: "inherit",
};
function btnGold(gold, goldL) {
  return { ...btnBase, background: `linear-gradient(135deg,${gold},${goldL})`, color: "#0d1f3c", boxShadow: `0 4px 14px rgba(201,151,28,0.40)` };
}
function btnGhost(D) {
  return { ...btnBase, background: D ? "rgba(255,255,255,0.06)" : "#f1f5f9", color: D ? "#f0f4f8" : "#0d1f3c", border: `1px solid ${D ? "rgba(255,255,255,0.10)" : "#e2e8f0"}` };
}
